/**
 * Switches for features that are ready on dev but not yet for schools.
 *
 * A switch is decided by the site the app is served from, so the same build
 * behaves correctly on both: on for local and the dev site, off on prod and
 * on any host not listed. To turn one on for prod, add 'app.shanen.li' to its
 * list. Nothing else has to change.
 */

/**
 * Hosts where the new Siddur reader (/siddur) is on. Prod since 17 Oct 2026:
 * the Siddur sits in the Book dropdown on the home card and opens the tree
 * reader; the old level-selector Siddur is gone for good.
 */
const SIDDUR_READER_HOSTS = ['localhost', '127.0.0.1', 'shanenli-web-app.fly.dev', 'app.shanen.li'];

const isLocalNetwork = (hostname: string): boolean =>
  /^192\.168\.\d+\.\d+$/.test(hostname) ||
  /^10\.\d+\.\d+\.\d+$/.test(hostname) ||
  /^172\.(1[6-9]|2[0-9]|3[0-1])\.\d+\.\d+$/.test(hostname);

/**
 * The new Siddur reader: a tree of gates, parts and prayers at /siddur.
 * When off, the Siddur is hidden: it is left out of the book list and /siddur
 * redirects home.
 */
export const isSiddurReaderEnabled = (
  hostname: string = window.location.hostname
): boolean => SIDDUR_READER_HOSTS.includes(hostname) || isLocalNetwork(hostname);

/**
 * Hosts where the voice level meter and its "speak up" bubble show on the
 * reading screen. On everywhere since 17 Oct 2026: Elie tested it on dev and
 * wants readers nudged to speak up. The quiet floor is tuned later from the
 * READING_VOICE_LEVEL rows (see utils/voice-level.ts).
 */
const VOICE_LEVEL_METER_HOSTS = ['localhost', '127.0.0.1', 'shanenli-web-app.fly.dev', 'app.shanen.li'];

/**
 * The voice level meter and speak-up bubble next to the microphone light.
 * On for local development, the dev site and prod.
 */
export const isVoiceLevelMeterEnabled = (
  hostname: string = window.location.hostname
): boolean => VOICE_LEVEL_METER_HOSTS.includes(hostname) || isLocalNetwork(hostname);

/**
 * Hosts where the READING_VOICE_LEVEL log row is NOT written. Empty: the row
 * has no visible effect, so it is written everywhere, prod included. Add a
 * host here to stop it there.
 */
const VOICE_LEVEL_LOGGING_OFF_HOSTS: string[] = [];

/** One READING_VOICE_LEVEL row per reading in ReadingLogs. On unless the host is listed above. */
export const isVoiceLevelLoggingEnabled = (
  hostname: string = window.location.hostname
): boolean => !VOICE_LEVEL_LOGGING_OFF_HOSTS.includes(hostname);
